import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Coins } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { CreditPackBreakdown } from "@/components/CreditPackBreakdown";

const BLANK = { name: "", price_usd: 5, sort: 0, active: true };

export default function AdminCreditPackages() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // package id or null
  const [form, setForm] = useState(BLANK);
  const price = Number(form.price_usd);
  const priceErr = !Number.isFinite(price) || price < 5 || price > 2000;
  const { data: quote, isFetching: quoting } = useQuery({
    queryKey: ["credit-package-quote", price],
    queryFn: async ({ signal }) => (await api.get("/payments/quote", { params: { usd: price }, signal })).data,
    enabled: open && !priceErr,
  });

  const { data: packs } = useQuery({
    queryKey: ["admin-credit-packages"],
    queryFn: async () => (await api.get("/admin/credit-packages")).data,
  });

  const openNew = () => { setEditing(null); setForm(BLANK); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ name: p.name, price_usd: p.price_usd, sort: p.sort || 0, active: p.active });
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form, price_usd: Number(form.price_usd), sort: Number(form.sort) };
      if (editing) return (await api.put(`/admin/credit-packages/${editing}`, payload)).data;
      return (await api.post("/admin/credit-packages", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-credit-packages"] });
      qc.invalidateQueries({ queryKey: ["credit-packages"] });
      setOpen(false);
      toast.success(editing ? "Package updated" : "Package created");
    },
    onError: (e) => toast.error(apiError(e, "Save failed")),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/credit-packages/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-credit-packages"] });
      qc.invalidateQueries({ queryKey: ["credit-packages"] });
      toast.success("Deleted");
    },
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-display text-3xl text-white tracking-[0.06em]">CREDIT PACKAGES</h1>
        <Button data-testid="admin-new-credit-pack" onClick={openNew} className="bg-emerald-400 text-black hover:bg-emerald-300">
          <Plus className="h-4 w-4 mr-1" /> New Package
        </Button>
      </div>
      <p data-testid="admin-credit-pricing-rule" className="text-white/45 text-sm mb-6">400 base credits per $1. Bonuses: $10+ 10%, $20+ 23%, $35+ 35%, $50+ 50%. Minimum $5. Every payment method uses the same pricing. Existing orders retain their saved quote.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(packs || []).map((p) => (
          <div key={p.id} data-testid={`admin-credit-pack-${p.id}`} className="hud-pill rounded-2xl p-4">
            <div className="flex items-start justify-between">
              <div>
                <div data-testid={`admin-credit-pack-name-${p.id}`} className="font-display text-xl text-white tracking-[0.06em]">{p.name}</div>
                {p.bonus_label && <div data-testid={`admin-credit-pack-label-${p.id}`} className="text-[10px] font-mono uppercase tracking-[0.15em] text-emerald-300 mt-0.5">{p.bonus_label}</div>}
                <div data-testid={`admin-credit-pack-total-${p.id}`} className="flex flex-wrap items-center gap-1.5 mt-2 text-amber-300 font-mono">
                  <Coins className="h-4 w-4" /> {p.credits.toLocaleString()} <span className="text-white/40">· ${p.price_usd.toFixed(2)}</span>
                </div>
                <CreditPackBreakdown pack={p} prefix={`admin-pack-${p.id}`} />
                {!p.active && <span className="inline-block mt-2 text-[10px] font-mono uppercase tracking-[0.15em] text-white/40 border border-white/15 rounded px-1.5 py-0.5">inactive</span>}
              </div>
              <div className="flex gap-1">
                <Button data-testid={`admin-credit-pack-edit-${p.id}`} title="Edit package" aria-label="Edit package" variant="ghost" size="sm" onClick={() => openEdit(p)} className="text-cyan-300"><Pencil className="h-4 w-4" /></Button>
                <Button data-testid={`admin-credit-pack-delete-${p.id}`} title="Delete package" aria-label="Delete package" variant="ghost" size="sm" onClick={() => { if (window.confirm("Delete package?")) del.mutate(p.id); }} className="text-red-400"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        ))}
        {(packs || []).length === 0 && <div className="col-span-full text-center text-white/40 py-12">No credit packages yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-credit-pack-dialog" className="bg-[hsl(222_22%_8%)] border-white/10 max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="admin-credit-pack-dialog-title" className="font-display text-2xl tracking-[0.08em]">{editing ? "EDIT PACKAGE" : "NEW PACKAGE"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Name</Label>
              <Input data-testid="credit-pack-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Price (USD)</Label>
                <Input data-testid="credit-pack-price" type="number" step="0.01" min="5" max="2000" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
                {priceErr && <div data-testid="credit-pack-price-error" role="alert" className="text-[10px] text-red-400 mt-1">Price must be $5–$2,000.</div>}
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Credits (calculated)</Label>
                <Input data-testid="credit-pack-credits" readOnly value={!priceErr && quote ? quote.credits.toLocaleString() : "—"} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Bonus (calculated)</Label>
                <Input data-testid="credit-pack-bonus" readOnly value={!priceErr && quote ? `+${Math.round(quote.bonus_pct * 100)}% bonus` : "—"} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Sort</Label>
                <Input data-testid="credit-pack-sort" type="number" value={form.sort} onChange={(e) => setForm({ ...form, sort: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch data-testid="credit-pack-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label className="text-white/70 text-sm">Active (shown in store)</Label>
            </div>
            <Button data-testid="credit-pack-save" onClick={() => save.mutate()} disabled={!form.name.trim() || priceErr || !quote || quoting || save.isPending} className="w-full bg-emerald-400 text-black hover:bg-emerald-300">
              {save.isPending ? "Saving…" : editing ? "Save Changes" : "Create"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
